import React, { useMemo } from 'react'
import ReactEChartsCore from './EChartsCore.js'
import * as echarts from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { rankedBarOption, type RankedBar } from './Options/rankedBarOption.js'
import { CHART_SURFACE } from './Options/visualIdentity.js'

echarts.use([BarChart, GridComponent, TooltipComponent, CanvasRenderer])

export type { RankedBar }

interface Props {
  /** Bars in ranked order (highest first); rendered top-to-bottom. */
  data: RankedBar[]
  onBarClick?: (name: string) => void
  xlabel?: string
  height?: number | `${number}%`
}

/**
 * ECharts HORIZONTAL bar chart for a top-N taxa ranking. Horizontal so long taxon names stay readable.
 * Callers pass bars pre-sorted DESCENDING; `yAxis.inverse` places the highest at the TOP. Clicking a
 * bar reports its taxon `name` (the sink drills into it via setTaxaZoom, mirroring the pie/treemap slices).
 *
 * The echarts `option` is built by the PURE `rankedBarOption` builder (Options/rankedBarOption.ts) so
 * this UI and a future server-side render sidecar share one source of truth.
 */
export const RankedBarChartNext: React.FC<Props> = ({ data, onBarClick, xlabel, height }) => {
  const option = useMemo(() => rankedBarOption({ data, xlabel }), [data, xlabel])

  const onEvents = useMemo(
    () => ({
      click: (p: { name?: string }) => {
        if (onBarClick && typeof p.name === 'string') onBarClick(p.name)
      }
    }),
    [onBarClick]
  )

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      onEvents={onEvents}
      notMerge
      lazyUpdate
      style={{ width: '100%', height: height ?? 400, backgroundColor: CHART_SURFACE }}
    />
  )
}
